/**
 * stylesPourLAgent — ce que l'agent lit des styles de paragraphe que le fichier
 * de l'acte définit : dans quel ordre les nommer, la liste que la lecture de
 * forme de tout l'acte en rend (`canvas_inspect` `formatOf` `all` ; quand,
 * `formePourLAgent.ts`), et le refus d'un style que le fichier ne définit pas.
 *
 * WHY un module à part de `formePourLAgent.ts` : la lecture de forme et le
 * refus d'une écriture nomment les mêmes styles dans le même ordre. Module pur.
 */

import {
  CANVAS_FORMAT_READ_STYLE_IDS_MAX,
  CANVAS_FORMAT_READ_STYLES_MAX,
  CANVAS_PARAGRAPH_FORMAT_KEYS,
  CANVAS_RUN_FORMAT_KEYS,
  type CanvasFormatRead,
  type CanvasParagraphFormat,
  type CanvasParagraphStyleRead,
  type CanvasRunFormat,
} from '@hector/shared-types';
import type { Reglage, StyleDeParagraphe } from '@hector/docx-forme';

/**
 * Ce que ces lectures prennent d'un acte : les styles que le fichier définit, et les paragraphes qui les portent —
 * l'acte lu comme ce qu'une cible vise (`FormeDeLActe`, `ActeVise`) : seul l'ordre d'usage des styles en dépend.
 */
interface StylesEtPorteurs {
  paragraphes: ReadonlyArray<{ styleId: string | null }>;
  stylesDeParagraphe: readonly StyleDeParagraphe[];
}

/** Nombre de styles qu'un refus nomme ; les autres sont comptés. Valeur de jugement : assez pour un gabarit d'avocat, sans noyer la cause du refus. */
export const STYLES_NOMMES_AU_REFUS_MAX = 30;

/**
 * Les styles de paragraphe du fichier, ceux que les paragraphes du corps
 * portent en tête — du plus porté au moins porté —, puis les autres dans
 * l'ordre du fichier. Une liste bornée garde ainsi les styles de l'acte.
 */
export function stylesDansLOrdreDUsage(acte: StylesEtPorteurs): Array<{ style: StyleDeParagraphe; paragraphes: number }> {
  const portes = new Map<string, number>();
  for (const { styleId } of acte.paragraphes) if (styleId !== null) portes.set(styleId, (portes.get(styleId) ?? 0) + 1);
  return acte.stylesDeParagraphe
    .map((style, rang) => ({ style, paragraphes: portes.get(style.id) ?? 0, rang }))
    .sort((a, b) => b.paragraphes - a.paragraphes || a.rang - b.rang)
    .map(({ style, paragraphes }) => ({ style, paragraphes }));
}

/**
 * Le message du refus d'un style que le fichier ne définit pas : l'éditeur le
 * refuserait paragraphe par paragraphe (`reglagesDeParagraphe.ts`, « Le style
 * nommé »), sans dire lesquels existent.
 */
export function refusDuStyleAbsent(verbe: string, styleId: string, acte: StylesEtPorteurs): string {
  const ranges = stylesDansLOrdreDUsage(acte);
  const nommes = ranges.slice(0, STYLES_NOMMES_AU_REFUS_MAX).map(({ style }) => (style.nom === null || style.nom === style.id ? style.id : `${style.id} ("${style.nom}")`));
  const enPlus = ranges.length - nommes.length;
  return `${verbe} was refused before any write: the document file defines no paragraph style with id ${JSON.stringify(styleId)}. Nothing was written. `
    + (nommes.length === 0
      ? 'The document file defines no paragraph style at all: tell the lawyer, and set the wanted form with the other settings.'
      : `styleId is the id of a style, not its name. Paragraph styles this document defines, the ones its paragraphs use first — id ("name"): ${nommes.join(', ')}`
        + `${enPlus > 0 ? `, and ${enPlus} more (canvas_inspect with formatOf {"kind":"all"} lists them)` : ''}. Resend with one of these ids, or tell the lawyer the style does not exist in this document.`);
}

/**
 * Ce qu'une forme tient d'un style nommé : les réglages d'origine `style`, et
 * ceux qui ne se lisent pas. Ce qui vient des défauts du document n'est pas
 * réglé par le style ; un réglage que rien ne définit (`non_defini`) non plus.
 */
function tenuDuStyle<Forme, Cle extends keyof Forme & string>(
  reglages: { [C in Cle]: Reglage<Forme[C]> },
  cles: readonly Cle[],
): { regles: Partial<Pick<Forme, Cle>>; nonLisibles: Cle[] } {
  const regles: Partial<Pick<Forme, Cle>> = {};
  const nonLisibles: Cle[] = [];
  for (const cle of cles) {
    const reglage = reglages[cle];
    if (reglage.lisible) {
      if (reglage.origine === 'style') regles[cle] = reglage.valeur;
    } else if (reglage.raison !== 'non_defini') nonLisibles.push(cle);
  }
  return { regles, nonLisibles };
}

/** Un style de paragraphe tel qu'une lecture de forme le rend (`CanvasParagraphStyleReadSchema`). */
function stylePourLAgent({ id, nom, heriteDe, forme, texte }: StyleDeParagraphe): CanvasParagraphStyleRead {
  const paragraphe = tenuDuStyle<CanvasParagraphFormat, (typeof CANVAS_PARAGRAPH_FORMAT_KEYS)[number]>(forme.paragraphe, CANVAS_PARAGRAPH_FORMAT_KEYS);
  const passage = tenuDuStyle<CanvasRunFormat, (typeof CANVAS_RUN_FORMAT_KEYS)[number]>(texte, CANVAS_RUN_FORMAT_KEYS);
  const [premier, ...autres] = [...paragraphe.nonLisibles, ...passage.nonLisibles];
  return {
    styleId: id,
    styleName: nom,
    ...(heriteDe === null ? {} : { basedOn: heriteDe }),
    ...(forme.numerotation === 'du_style' ? { numbered: true as const } : {}),
    ...(Object.keys(paragraphe.regles).length === 0 ? {} : { paragraph: paragraphe.regles }),
    ...(Object.keys(passage.regles).length === 0 ? {} : { run: passage.regles }),
    ...(premier === undefined ? {} : { unreadable: [premier, ...autres] }),
  };
}

/**
 * Les styles de paragraphe du fichier, au format d'une lecture de forme, dans
 * l'ordre d'usage ({@link stylesDansLOrdreDUsage}) : les
 * {@link CANVAS_FORMAT_READ_STYLES_MAX} premiers avec ce qu'ils règlent, les
 * {@link CANVAS_FORMAT_READ_STYLE_IDS_MAX} suivants par leur identifiant, et le
 * nombre de ceux qui restent.
 */
export function stylesPourLAgent(acte: StylesEtPorteurs): Required<Pick<CanvasFormatRead, 'paragraphStyles'>> & Pick<CanvasFormatRead, 'otherParagraphStyleIds' | 'paragraphStylesNotListed'> {
  const ranges = stylesDansLOrdreDUsage(acte);
  const [premierSuivant, ...autresSuivants] = ranges.slice(CANVAS_FORMAT_READ_STYLES_MAX, CANVAS_FORMAT_READ_STYLES_MAX + CANVAS_FORMAT_READ_STYLE_IDS_MAX).map(({ style }) => style.id);
  const nonRendus = ranges.length - CANVAS_FORMAT_READ_STYLES_MAX - CANVAS_FORMAT_READ_STYLE_IDS_MAX;
  return {
    paragraphStyles: ranges.slice(0, CANVAS_FORMAT_READ_STYLES_MAX).map(({ style }) => stylePourLAgent(style)),
    ...(premierSuivant === undefined ? {} : { otherParagraphStyleIds: [premierSuivant, ...autresSuivants] }),
    ...(nonRendus > 0 ? { paragraphStylesNotListed: nonRendus } : {}),
  };
}
